import {Injectable} from '@angular/core';
import {JobPreview} from "../interfaces/job-preview";
import {ImageService} from "../atoms/image/image.service";
import {ItemActionElement} from "../atoms/item-actions/item-action-element";
import {isEntityInFavorites} from "../store/selectors/user.selector";
import {take} from "rxjs";
import {toggleFavorite} from "../store/actions/user.actions";
import {ShareOverlayComponent} from "../shared/share-overlay/share-overlay.component";
import {AppState} from "../store";
import {OverlayService} from "../overlay/overlay.service";
import {Store} from "@ngrx/store";
import {NotificationService} from "../atoms/notification/notification.service";
import {JobDetails} from "../interfaces/job-details";
import {CdColor} from "../enums/cd-color";
import {resetFiltersByEntity, setJobFilter} from "../store/actions/filter.actions";
import {FavoriteEntityType} from "../enums/favorite-entity-type";
import {ProvidersService} from "./providers.service";
import {GetGlobalSearchResults_globalSearch_jobMarkets} from "../queries/__generated__/GetGlobalSearchResults";
import {GlobalSearchResult} from "../search-filter/global-search-overlay/global-search-result";
import {Image} from "../interfaces/image";
import {GlobalSearchResultType} from "../enums/global-search-result-type";
import {JobState} from "../enums/job-state";
import {JobStartType} from "../enums/job-start-type";
import {JobViewType} from "../enums/job-view-type";
import {JobApplyType} from "../enums/job-apply-type";
import {JobMarket, JobMarketViewType} from "../interfaces/backend/job-market";
import {ContactPerson} from "../interfaces/backend/contact-person";
import {JobMarketAdditionalInfo} from "../interfaces/backend/job-market-additional-info";
import {Contact} from "../interfaces/backend/contact";
import {Address} from "../interfaces/backend/address";
import {JobSimple} from "../interfaces/backend/job-simple";
import {CompanyProfileSimple} from "../interfaces/backend/company-profile-simple";
import {PositionType} from "../enums/position-type";

// Common shape of JobMarket (GET_JOB_MARKETS_BY_USER) and JobMarketInternal (GET_JOB_BY_ID, GET_JOB_BY_ID_INTERNAL)
type RawJobDetails = Omit<JobMarket, "viewCount" | "applicationCount" | "contact"> & {
  viewType?: JobMarketViewType | null;
  viewCount?: number;
  applicationCount?: number;
  contact: (ContactPerson & {id?: string}) | null;
};

@Injectable({
  providedIn: 'root'
})
export class JobService {

  private readonly SINGLE_PAGE_ENDPOINT = 'jobboerse';
  private readonly JOBS_OVERVIEW_PAGE_ENDPOINT = "jobboerse";
  public readonly JOBS_HASH_FILTER_PREFIX = "jobsFilter";
  constructor(
    private image: ImageService,
    private store: Store<AppState>,
    private overlay: OverlayService,
    private notifications: NotificationService,
    private providerService: ProvidersService
  ) { }

  mapRawJobPreview(rawJobPreview: any): JobPreview {
    const jobPreview: JobPreview = {
      addresses: rawJobPreview.addresses,
      associatedProfessions: rawJobPreview.jobs,
      companyProfile: rawJobPreview.companyProfile,
      id: parseInt(rawJobPreview.id),
      image: this.image.mapRawImageData(rawJobPreview.image),
      name: rawJobPreview.name,
      positionType: rawJobPreview.positionType,
      interestFields: []
    }

    if(rawJobPreview.jobs && rawJobPreview.jobs.length) {
      rawJobPreview.jobs.forEach((profession: any) => {
        if(profession.interestFields && profession.interestFields.length) {
          profession.interestFields.forEach((interestField: any) => {
            jobPreview.interestFields = this.pushUniqueNumber(jobPreview.interestFields, parseInt(interestField.id));
          })
        }
      })
    }

    return jobPreview;
  }

  mapRawJobDetails(rawJobDetails: RawJobDetails): JobDetails {

    // The contact is reshaped (communication -> entries, person gets an avatar), the result does not
    // fully match the interface Contact (e.g. person.titles instead of person.title), hence the cast below
    const rawContact = rawJobDetails.contact;
    const contact: Record<string, unknown> = {...rawContact};
    if(rawContact?.communication) {
      contact["communication"] = rawContact.communication.entries;
    }
    if(rawContact?.person) {
      contact["person"] = {
        ...rawContact.person,
        avatar: this.image.mapRawImageData(rawContact.picture, "person")
      }
    }
    contact["employeePosition"] = rawContact?.employeePosition ?? "";
    contact["id"] = parseInt(rawContact?.id ?? "-1");

    // The casts below keep the runtime values unchanged: JobDetails is stricter than the backend data
    // (non-null strings/numbers, enums instead of string literals, numeric ids, more address fields)
    const jobDetails: JobDetails = {
      additionalInfo: rawJobDetails.additionalInfo as JobMarketAdditionalInfo,
      applyUntil: rawJobDetails.applyUntil as string,
      closeDate: rawJobDetails.closeDate as string,
      state: (rawJobDetails.state as JobState) ?? JobState.ACTIVE,
      contact: contact as unknown as Contact,
      viewCount: rawJobDetails.viewCount ?? 0,
      viewType: (rawJobDetails.viewType as JobViewType | null | undefined) ?? JobViewType.DEFAULT,
      applicationCount: rawJobDetails.applicationCount ?? 0,
      description: rawJobDetails.description ?? "",
      details: rawJobDetails.details ?? "",
      numberOfPositions: rawJobDetails.numberOfPositions as number,
      openDate: rawJobDetails.openDate as string,
      requirements: rawJobDetails.requirements ?? "",
      salary: rawJobDetails.salary ?? "",
      startDate: rawJobDetails.startDate as string,
      addresses: rawJobDetails.addresses as unknown as Address[],
      applyType: rawJobDetails.applyType as JobApplyType,
      associatedProfessions: (rawJobDetails.jobs ?? []) as unknown as JobSimple[],
      companyProfile: rawJobDetails.companyProfile as unknown as CompanyProfileSimple,
      id: parseInt(rawJobDetails.id),
      image: this.image.mapRawImageData(rawJobDetails.image),
      name: rawJobDetails.name,
      positionType: rawJobDetails.positionType as PositionType,
      onlineUrl: rawJobDetails.onlineUrl ?? "",
      mediaGallery: this.providerService.parseMediaGallery(rawJobDetails.companyProfile?.mediaGallery),
      benefits: this.providerService.parseCompanyBenefits(rawJobDetails.companyProfile?.companyBenefits),
      companyProfilePicture: this.image.mapRawImageData(rawJobDetails.companyProfile?.companyProfilePicture),
      startType: rawJobDetails.startType as JobStartType,
      interestFields: [],
      customMedia: {
        image: null,
        document: {
          filePath: rawJobDetails.customMedia?.document?.filePath ?? ""
        }
      }
    }

    if(rawJobDetails.customMedia?.image) {
      jobDetails.customMedia.image = this.image.mapRawImageData(rawJobDetails.customMedia?.image);
    }

    if(rawJobDetails.jobs && rawJobDetails.jobs.length) {
      rawJobDetails.jobs.forEach((profession) => {
        if(profession.interestFields && profession.interestFields.length) {
          profession.interestFields.forEach((interestField) => {
            jobDetails.interestFields = this.pushUniqueNumber(jobDetails.interestFields, parseInt(interestField.id));
          })
        }
      })
    }

    return jobDetails;
  }

  private pushUniqueNumber(arr: number[], id: number) {
    if(!arr.includes(id)) {
      arr.push(id);
    }
    return arr;
  }

  getFullUrl(id: number): string {
    return `${window.location.origin}/${this.SINGLE_PAGE_ENDPOINT}/${id}`;
  }

  getDetailPageUrlById(id: number): string {
    return `/${this.SINGLE_PAGE_ENDPOINT}/${id}`;
  }

  async generateMenuItems(preview: JobPreview): Promise<ItemActionElement[]> {
    const isFavorite = await new Promise<boolean>((resolve, reject) => {
      this.store.select(isEntityInFavorites(preview.id)).pipe(take(1)).subscribe((value) => {
        resolve(value);
      });
    });
    const menuItems: ItemActionElement[] = [
      {
        func: () => {
          if(!isFavorite) {
            this.notifications.push("Gemerkt", CdColor.RIVER, "check-circle");
          } else {
            this.notifications.push("Aus Merkliste entfernt", CdColor.RIVER, "check-circle");
          }

          this.store.dispatch(toggleFavorite({entityType: FavoriteEntityType.JOB, id: preview.id}));
        },
        title: isFavorite ? 'Gemerkt' : 'Merken',
        icon: isFavorite ? 'bookmark-full' : 'bookmark-outline',
        isActive: true
      },
      {
        func: () => {
          window.navigator.clipboard.writeText(this.getFullUrl(preview.id));
          this.notifications.push("Link kopiert", CdColor.RIVER, 'check-circle');
        },
        title: "Link kopieren",
        icon: "link",
        isActive: true
      },
      {
        func: () => {
          this.overlay.openWindowOverlay({componentMain: {component: ShareOverlayComponent, data: {url: this.getFullUrl(preview.id)}}, title: "Teilen " + preview.name})
          // alert("Not implemented")
        },
        title: 'Teilen',
        icon: "share",
        isActive: true
      }
    ];

    return menuItems;
  }


  getBaseOverviewUrl(): string {
    return this.JOBS_OVERVIEW_PAGE_ENDPOINT;
  }

  getOverviewUrlByPageIdx(idx: number): string {
    return `${this.getBaseOverviewUrl()}${idx === 1 ? '' : '/seite/' + idx}`;
  }

  /** Result looks like /jobboerse/page/3#asdsA234dfDa */
  /** Result looks like /jobboerse#asdsA234dfDa */
  /** Result looks like /jobboerse */
  /** Result looks like /jobboerse/page/3 */
  getFilterUrl(pageIdx: number, offerTypes: number[], interestFields: number[], searchTerm: string, districtIDs: number[], locationTerm: string, distance: number, sortBy: string, maturaOnly: boolean, partialQualification: boolean, relatedProfessionBicId: number, userLocation: {lat: number, lng: number}): string {
    if(distance < 0) distance = 0;
    let baseUrl = this.getOverviewUrlByPageIdx(pageIdx);
    let queryString = "";
    if(offerTypes.length) {
      queryString = `${queryString}selectedOfferTypes=${offerTypes.join(",")}&`;
    }

    if(interestFields.length) {
      queryString = `${queryString}selectedJobInterestFields=${interestFields.join(",")}&`;
    }

    if(partialQualification) {
      queryString = `${queryString}jobsPartialQualificationOnly=1&`;
    }

    if(maturaOnly) {
      queryString = `${queryString}jobsApprenticeshipMaturaOnly=1&`;
    }

    if(sortBy.length && sortBy !== "name-asc") { // like skip default sorting
      queryString = `${queryString}jobsArchiveSortBy=${sortBy}&`;
    }

    if(searchTerm.length) {
      queryString = `${queryString}searchTermJob=${searchTerm}&`;
    }

    if(districtIDs.length) {
      queryString = `${queryString}activeJobDistrictIds=${districtIDs.join(",")}&`;
    }

    if(locationTerm.length) {
      queryString = `${queryString}searchTermJobLocation=${locationTerm}&`;
    }

    if(distance) {
      queryString = `${queryString}jobsDistance=${distance}&`;
    }

    if(relatedProfessionBicId > 0) {
      queryString = `${queryString}relatedJobProfessionBicId=${relatedProfessionBicId}&`;
    }

    if(userLocation.lat && userLocation.lng) {
      queryString = `${queryString}userLocationJob=${userLocation.lat},${userLocation.lng}&`;
    }

    if(queryString.length) {
      // console.log(queryString);
      queryString = `${this.JOBS_HASH_FILTER_PREFIX}:${queryString}`;
      queryString = btoa(queryString);
      baseUrl = `${baseUrl}#${queryString}`;
    }

    return baseUrl;
  }

  setFiltersFromUrl(): void {

    let hash = window.location.hash.slice(1);
    if(hash.length) {
      this.store.dispatch(resetFiltersByEntity({key: 'allJob'}));

      try {
        hash = atob(hash);
        const splitHash = hash.split(":");
        splitHash.shift();
        hash = splitHash.join("");
        const splitByFilters = hash.split("&");
        // console.log(splitByFilters);
        splitByFilters.forEach((filter) => {
          if(filter.length) {
            const splitFilter = filter.split("=");
            if(splitFilter.length === 2) {
              const filterName = splitFilter[0];
              switch (filterName) {
                // array integers type
                case "selectedOfferTypes": case "activeJobDistrictIds": case "selectedJobInterestFields":
                  const splitValues = splitFilter[1].split(",");
                  this.store.dispatch(setJobFilter({key: filterName, value: splitValues.map((value) => parseInt(value))}));
                  break;
                // single string value type
                case "searchTermJob": case "jobsArchiveSortBy": case "searchTermJobLocation":
                  console.warn(splitFilter[1]);
                  this.store.dispatch(setJobFilter({key: filterName, value: splitFilter[1]}));
                  break;
                // integer type
                case "jobsDistance": case "relatedJobProfessionBicId":
                  const intValue = parseInt(splitFilter[1]);
                  if(!isNaN(intValue)) {
                    this.store.dispatch(setJobFilter({key: filterName, value: intValue}));
                  }
                  break;
                // true/false type
                case "jobsApprenticeshipMaturaOnly": case "jobsPartialQualificationOnly":
                  const boolValue = splitFilter[1] !== '0';
                  this.store.dispatch(setJobFilter({key: filterName, value: boolValue}));
                  break;
                case "userLocationJob":
                  let strLocation = splitFilter[1];
                  if(strLocation.length > 3 && strLocation.indexOf(",") !== -1) {
                    let arrLocation = strLocation.split(',');
                    if(arrLocation.length === 2) {
                      this.store.dispatch(setJobFilter({key: filterName, value: {lat: parseFloat(arrLocation[0]), lng: parseFloat(arrLocation[1])}}));
                    }
                  }
                  break;
              }
            }
          }

        })
      } catch (err: any) {
        console.warn(err);
      }
    }
  }

  mapGlobalSearchResult(rawData: GetGlobalSearchResults_globalSearch_jobMarkets): GlobalSearchResult {
    let type: GlobalSearchResultType = GlobalSearchResultType.HOLIDAY_JOB;
    if(rawData.positionType === "LEHRSTELLE") type = GlobalSearchResultType.APPRENTICESHIP;
    else if(rawData.positionType === "PRAKTIKUM") type = GlobalSearchResultType.INTERNSHIP;
    return {
      id: rawData.id,
      image: rawData.image as unknown as Image,
      quote: rawData.description ?? "",
      title: rawData.name ?? "",
      type: type,
      isGreen: false
    }
  }

  validateJob(job: JobDetails): boolean {
    // console.log(job);
    if(!job.name.length) return false;
    if(!job.openDate?.length) return false;
    if(!job.closeDate?.length) return false;
    if(!job.positionType) return false;
    if(!job.applyType) return false;
    if(!(job.customMedia.document.filePath.length || (job.description.length && job.requirements.length))) return false;
    if(!job.associatedProfessions.length) return false;
    if(!job.salary?.length) return false;
    if(!job.startType) return false;
    if(job.startType === JobStartType.DATE && !job.startDate?.length) return false;
    if(!job.applyUntil?.length) return false;
    if(!job.contact || job.contact.id === -1) return false;
    if((job.applyType === JobApplyType.ONLINE || job.applyType === JobApplyType.EMAIL) && !job.onlineUrl.length) return false;
    return true;
  }
}
